import type { WordDTO } from "@shared/types";

/**
 * Deterministic Quizzle question generator. Given the shared (pool, seed,
 * questionIndex, enabledTypes) — all carried in room state — every device
 * (host + every controller) computes the EXACT same question + options
 * without anything being sent per-question.
 */

export type QuizQuestionKind = "audio-to-image" | "audio-to-english" | "image-to-word" | "spell";

export interface QuizQuestion {
  kind: QuizQuestionKind;
  /** The correct word. */
  word: WordDTO;
  /** Multiple-choice options (incl. the correct word), deterministically
   *  shuffled. Empty for the `spell` kind. */
  options: WordDTO[];
}

/** Mulberry32 — tiny deterministic PRNG. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

/** Image-based kinds need words that actually have a picture. */
function kindNeedsImage(kind: QuizQuestionKind): boolean {
  return kind === "audio-to-image" || kind === "image-to-word";
}

export function buildQuestion(
  pool: WordDTO[],
  seed: number,
  index: number,
  enabledTypes: string[],
): QuizQuestion | null {
  if (!pool.length) return null;
  const types = (enabledTypes.filter((t): t is QuizQuestionKind =>
    t === "audio-to-image" || t === "audio-to-english" || t === "image-to-word" || t === "spell",
  ));
  const allowed = types.length ? types : (["audio-to-english"] as QuizQuestionKind[]);

  // Stable word ordering so the index→word mapping matches across devices.
  const ordered = [...pool].sort((a, b) => a.id.localeCompare(b.id));
  // EVEN COVERAGE (not random): shuffle the word order ONCE deterministically
  // (by seed) then walk it in sequence — every selected word is asked before
  // any repeats, so all words in the chosen packs get tested fairly.
  const order = shuffleInPlace([...ordered], mulberry32(seed >>> 0));
  const word = order[index % order.length]!;

  // Per-question RNG (seed mixed with index) drives the question TYPE +
  // distractor picks — deterministic across devices, but varied per question.
  const rng = mulberry32((seed ^ (index * 0x9e3779b1)) >>> 0);
  let kind = allowed[Math.floor(rng() * allowed.length)]!;

  // If the chosen kind needs a picture but the word has none, fall back to a
  // text/audio kind so the question is always renderable.
  if (kindNeedsImage(kind) && !word.imageUrl) kind = "audio-to-english";
  // Spelling needs ≥2 letters.
  if (kind === "spell" && word.english.replace(/[^A-Za-z]/g, "").length < 2) kind = "audio-to-english";

  let options: WordDTO[] = [];
  if (kind !== "spell") {
    const others = ordered.filter((w) => w.id !== word.id);
    // For image-answer questions, distractors also need an image.
    const pickFrom = kind === "audio-to-image" ? others.filter((w) => w.imageUrl) : others;
    const distractors = shuffleInPlace([...pickFrom], rng).slice(0, 3);
    options = shuffleInPlace([word, ...distractors], rng);
  }
  void word;
  return { kind, word, options };
}
